/**
 * FeedbackScreen.jsx
 * Post-appointment feedback flow with star rating and optional comment
 */
import React, { useState } from 'react';
import { Star, Send, X, MessageSquare, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const FeedbackScreen = ({ appointment, portalToken, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [trainerRating, setTrainerRating] = useState(0);
  const [hoverTrainerRating, setHoverTrainerRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error(t('feedback.please_rate'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/feedback`, {
        appointment_id: appointment.id,
        rating,
        comment: comment.trim(),
        trainer_rating: trainerRating || null,
      }, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setSubmitted(true);

      // Trigger native App Store review after positive feedback (4-5 stars)
      if (rating >= 4) {
        try {
          const { AppReview } = await import('@capacitor/app-review');
          await AppReview.requestReview();
        } catch {
          // Silently fail — web or plugin not available
        }
      }

      setTimeout(() => {
        onSubmitted?.();
        onClose();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('feedback.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950/95 flex items-center justify-center p-6" data-testid="feedback-success">
        <div className="text-center space-y-4 animate-in fade-in">
          <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
            <Heart className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-white text-xl font-bold">{t('feedback.thank_you')}</h2>
          <p className="text-zinc-400 text-sm">{t('feedback.thank_you_message')}</p>
        </div>
      </div>
    );
  }

  const formatAppointmentDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/95 flex flex-col" data-testid="feedback-screen">
      {/* Header */}
      <div className="p-4 flex items-center justify-between border-b border-zinc-800">
        <h2 className="text-white font-bold text-lg">{t('feedback.title')}</h2>
        <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white" data-testid="feedback-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-6">
        {/* Appointment Info */}
        <div className="bg-zinc-800/60 rounded-2xl p-4 border border-zinc-700/50">
          <p className="text-zinc-400 text-xs mb-1">{t('feedback.your_appointment')}</p>
          <p className="text-white font-semibold">{appointment.service_name || 'Training'}</p>
          {appointment.trainer_name && (
            <p className="text-yellow-400 text-sm mt-0.5">{t('feedback.with_trainer', { name: appointment.trainer_name })}</p>
          )}
          <p className="text-zinc-500 text-xs mt-1">{formatAppointmentDate(appointment.start_time)}</p>
        </div>

        {/* Overall Rating */}
        <div className="space-y-3">
          <p className="text-white font-medium">{t('feedback.how_was_it')}</p>
          <div className="flex items-center justify-center gap-2" data-testid="feedback-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`feedback-star-${star}`}
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    star <= (hoverRating || rating)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-zinc-600'
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm text-yellow-400">
              {rating === 5 && t('feedback.rating_5')}
              {rating === 4 && t('feedback.rating_4')}
              {rating === 3 && t('feedback.rating_3')}
              {rating === 2 && t('feedback.rating_2')}
              {rating === 1 && t('feedback.rating_1')}
            </p>
          )}
        </div>

        {/* Trainer Rating (if applicable) */}
        {appointment.trainer_name && (
          <div className="space-y-3">
            <p className="text-white font-medium">{t('feedback.trainer_rating', { name: appointment.trainer_name })}</p>
            <div className="flex items-center justify-center gap-2" data-testid="feedback-trainer-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setTrainerRating(star)}
                  onMouseEnter={() => setHoverTrainerRating(star)}
                  onMouseLeave={() => setHoverTrainerRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`feedback-trainer-star-${star}`}
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      star <= (hoverTrainerRating || trainerRating)
                        ? 'text-blue-400 fill-blue-400'
                        : 'text-zinc-600'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comment */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-zinc-400" />
            <p className="text-white font-medium text-sm">{t('feedback.comment_label')}</p>
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('feedback.comment_placeholder')}
            className="w-full bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-white text-sm placeholder-zinc-500 resize-none focus:outline-none focus:border-yellow-500/50"
            rows={3}
            data-testid="feedback-comment"
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="p-4 border-t border-zinc-800">
        <button
          onClick={handleSubmit}
          disabled={rating === 0 || submitting}
          className="w-full py-3.5 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
          data-testid="feedback-submit-btn"
        >
          <Send className="w-5 h-5" />
          {submitting ? t('feedback.submitting') : t('feedback.submit')}
        </button>
      </div>
    </div>
  );
};

export default FeedbackScreen;
